// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import dynamicImportPolyfill from "dynamic-import-polyfill";
import browserIsFirefox from "../../utils/is-firefox";
import browserIsSafari from "../../utils/is-safari";
import browserIsWkWebview from "../../utils/is-wkwebview";
import promiseWithTimeout from "../../utils/timeout";
import Service from "../service";
import getLocationV1 from "./geo-v1";
import { isGeoClient } from "./helpers";
const DEFAULT_CLIENT_URL = "https://cdn.almamedia.tech/esm/geo-client/v5/geo-client.min.mjs";
class GeoService extends Service {
    constructor(context, config) {
        super("Geo", context);
        this.geoClientUrl = DEFAULT_CLIENT_URL;
        this.useV1 = false;
        if (config?.customClient) {
            this.geoClientUrl = config.customClient;
        }
        this.useV1 = config?.useV1 ?? false;
        this.logger.logInfo(`Using client: ${this.geoClientUrl}`);
    }
    async bootstrapService() {
        this.logger.logInfo(`Bootstrapping Geo service`);
        this.logger.logInfo(`Checking User-Agent`);
        const isSafari = await browserIsSafari();
        const isFirefox = await browserIsFirefox();
        const isWkWebview = await browserIsWkWebview();
        if (isSafari || isFirefox || isWkWebview) {
            this.logger.logInfo(`Geo disabled due to UA detection`);
            return undefined;
        }
        this.logger.logInfo(`User-Agent probably doesn't have an annoying geo modal`);
        if (this.useV1 === true) {
            this.logger.logInfo(`V1 in use, don't load client`);
            return undefined;
        }
        try {
            // This needs to be done before any dynamic imports are used.
            if (dynamicImportPolyfill) {
                dynamicImportPolyfill.initialize({});
            }
            if (typeof window.__import__ !== "function") {
                this.logger.logInfo(`Geo not available due to missing dynamic import`);
                return undefined;
            }
            if (!this.client) {
                this.logger.logInfo(`Client not ready, importing`);
                this.client = await window
                    .__import__(this.geoClientUrl)
                    .then(({ default: geo }) => {
                    this.logger.logInfo(`Module ready`);
                    if (isGeoClient(geo)) {
                        return geo;
                    }
                    this.logger.logInfo(`Imported module does not seem to be Alma Geo Client`);
                    return undefined;
                })
                    .catch((err) => {
                    this.logger.logWarn(err);
                    return undefined;
                });
            }
        }
        catch (e) {
            this.logger.logError(`Error occured while bootstrapping geo: ${e}`);
        }
        return undefined;
    }
    async getLocationImpl(timeout = 1000, acceptedCacheAge) {
        const locationDisabled = this.context.services.dataLayerService?.dataLayer?.locationDisabled ??
            window?.digitalData?.page?.attributes?.locationDisabled ??
            false;
        if (locationDisabled === true) {
            this.logger.logInfo(`Location request disabled from DDL`);
            return undefined;
        }
        if (this.useV1 === true) {
            this.logger.logDebug(`Using V1 for geo`);
            let res;
            try {
                res = (await getLocationV1(timeout, true));
            }
            catch (e) {
                this.logger.logError(`GeoV1 failed: ${e}`);
            }
            return res;
        }
        if (!this.client) {
            this.logger.logInfo(`No client available`);
            return undefined;
        }
        this.logger.logInfo(`Accessing Geo`);
        try {
            this.logger.logInfo(`Requesting location with timeout ${timeout}, accepted cache age ${acceptedCacheAge}`);
            const options = { timeout, acceptedCacheAge };
            const result = await this.client.getLocationApproximation(options);
            if (result instanceof Error) {
                this.logger.logError(`Error during geoloc: ${result}`);
                return undefined;
            }
            return result;
        }
        catch (e) {
            this.logger.logInfo(`Unable to get location (${e})`);
            return undefined;
        }
    }
    async getLocation(timeout = 1000, acceptedCacheAge) {
        return promiseWithTimeout(timeout, this.getLocationImpl(timeout, acceptedCacheAge), "Geo ATS timeout");
    }
}
export default GeoService;
//# sourceMappingURL=geo.js.map